import { useNodeManager } from '@/composables/useNodeManager';
import eventBus from '@/utils/eventBus';
import * as THREE from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { scene } from '../core';
import { renderer } from '../core/renderer';
import { transformControls } from '../handlers/transform';
import { BaseObject } from './base';

export class Annotation extends BaseObject {
  constructor(properties) {
    super();
    const group = new THREE.Group();

    // Set userData for the annotation group
    group.userData.type = 'annotation';
    group.userData.code = properties.code;
    group.userData.selectable = true;

    // Create distance label element
    const distanceLabel = document.createElement('div');
    distanceLabel.classList.add(
      'text-gray-300',
      'text-sm',
      'pointer-events-none',
      'line-clamp-2',
      'overflow-hidden',
      'whitespace-pre-line'
    );

    // Create edit button
    const editButton = document.createElement('button');
    editButton.textContent = 'Edit';
    editButton.classList.add(
      'ml-2',
      'p-1',
      'py-0.5',
      'bg-primary',
      'text-gray-900',
      'text-xs',
      'rounded',
      'cursor-pointer',
      'shadow-lg',
      'pointer-events-auto',
      'hover:scale-105',
      'transition-all',
      'delay-100',
      'hidden'
    );
    editButton.onclick = (e) => {
      e.stopPropagation();
      eventBus.emit('edit-annotation', properties.code);
    };

    // Create container for label and button
    const labelContainer = document.createElement('div');
    labelContainer.classList.add(
      'max-w-4xl',
      'flex',
      'items-center',
      'bg-black/60',
      'p-2',
      'rounded',
      'tracking-wider',
      'pointer-events-auto',
      'cursor-pointer'
    );
    labelContainer.dataset.annotationLabel = '';

    const _onMouseEnter = () => {
      labelContainer.classList.add('max-h-fit');
      distanceLabel.classList.remove('line-clamp-2');
    };
    const _onMouseLeave = () => {
      labelContainer.classList.remove('max-h-fit');
      distanceLabel.classList.add('line-clamp-2');
    };
    labelContainer.addEventListener('mouseenter', _onMouseEnter);
    labelContainer.addEventListener('mouseleave', _onMouseLeave);

    labelContainer.appendChild(distanceLabel);
    labelContainer.appendChild(editButton);

    const distanceLabelObject = new CSS2DObject(labelContainer);

    // Create the target object for the annotation
    const target = new THREE.Object3D();
    target.position.copy(properties.data.target);
    target.userData.isTarget = true;
    target.userData.isAnnotationTarget = true;
    target.userData.forUuid = properties.code;
    target.userData.annotationCode = properties.code;
    target.userData.code = properties.code;
    target.userData.selectable = true;
    target.name = properties.code;
    // Add the label as a child of target so it follows when TransformControls moves it
    target.add(distanceLabelObject);
    scene.add(target);
    group.target = target;

    // Create an invisible sphere mesh for target selection
    const targetSphereGeometry = new THREE.SphereGeometry(0.3, 8, 8);
    const targetSphereMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
    });
    const targetSelectionSphere = new THREE.Mesh(targetSphereGeometry, targetSphereMaterial);

    // Make the sphere selectable and link it to the annotation
    targetSelectionSphere.userData.selectable = true;
    targetSelectionSphere.userData.isAnnotationTarget = true;
    targetSelectionSphere.userData.annotationCode = properties.code;
    targetSelectionSphere.userData.projectorOverlayIgnore = true;
    targetSelectionSphere.renderOrder = 999;

    // Add selection sphere to target
    target.add(targetSelectionSphere);

    distanceLabel.textContent = properties.name;

    // Flag to track if we just finished dragging
    let justFinishedDragging = false;

    // Transform controls attachment function
    const attachTransformControls = () => {
      if (!transformControls || !target) return;

      transformControls.attach(target);
      transformControls.setMode('translate');
      transformControls.showX = true;
      transformControls.showY = true;
      transformControls.showZ = true;

      const gizmo = transformControls.getHelper();
      if (gizmo) {
        gizmo.traverse((object) => {
          object.layers.set(1);
          if ('isMesh' in object && object.isMesh) {
            object.userData.projectorOverlayIgnore = true;
          }
        });
        if (!gizmo.parent) {
          scene.add(gizmo);
        }
      }

      // Add mouseUp listener to save position changes
      const onMouseUp = () => {
        if (transformControls.object === target) {
          const { updateNode } = useNodeManager();
          const updatedPosition = target.position.clone();

          updateNode(
            properties.code,
            {
              target: {
                x: updatedPosition.x,
                y: updatedPosition.y,
                z: updatedPosition.z,
              },
            },
            true
          );

          // Set flag to prevent immediate clearing
          justFinishedDragging = true;
          setTimeout(() => {
            justFinishedDragging = false;
          }, 100);
        }
      };

      // Remove old listener if exists
      if (target.userData.mouseUpListener) {
        transformControls.removeEventListener('mouseUp', target.userData.mouseUpListener);
      }

      // Store and add new listener
      target.userData.mouseUpListener = onMouseUp;
      transformControls.addEventListener('mouseUp', onMouseUp);
    };

    // Store the attach function for reuse
    target.userData.attachTransformControls = attachTransformControls;

    // Label container event handlers
    labelContainer.onclick = () => {
      if (target.userData.attachTransformControls) {
        target.userData.attachTransformControls();
      }
    };

    labelContainer.onmouseenter = () => {
      editButton.classList.remove('hidden');
    };

    labelContainer.onmouseleave = () => {
      editButton.classList.add('hidden');
    };

    // Handle clicks on the scene to clear gizmo
    const handleSceneClick = (event) => {
      // Check if the click is on the scene canvas
      if (event.target === renderer.domElement) {
        // Don't clear if currently dragging or just finished dragging
        if (transformControls && (transformControls.dragging || justFinishedDragging)) {
          return;
        }

        // Check if clicking on the gizmo itself
        if (transformControls && transformControls.object === target) {
          const gizmo = transformControls.getHelper();
          if (gizmo && transformControls.axis !== null) {
            // User is interacting with the gizmo, don't clear
            return;
          }

          // Check if clicking on the annotation target or label
          const rect = renderer.domElement.getBoundingClientRect();
          const mouse = new THREE.Vector2();
          mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
          mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

          const raycaster = new THREE.Raycaster();
          raycaster.setFromCamera(mouse, transformControls.camera || scene.children.find((c) => c.isCamera));

          // Check if we hit the annotation target or its children
          const intersects = raycaster.intersectObject(target, true);
          if (intersects.length > 0) {
            // Clicked on the annotation, don't clear
            return;
          }

          // Check if clicking on the label container
          if (event.target !== renderer.domElement) {
            return;
          }

          // Clear selection when clicking elsewhere on canvas
          group.clearSelection();
        }
      }
    };

    // Store the handler for cleanup
    group.userData.sceneClickHandler = handleSceneClick;
    document.addEventListener('click', handleSceneClick);

    group.applyProperties = (properties) => {
      if ('text' in properties) {
        distanceLabel.textContent = properties.text;
      }
      if ('target' in properties) {
        target.position.set(properties.target.x, properties.target.y, properties.target.z);
      }
    };

    // Cleanup on remove
    const originalRemove = group.remove.bind(group);
    group.remove = () => {
      // Remove scene click handler
      if (group.userData.sceneClickHandler) {
        document.removeEventListener('click', group.userData.sceneClickHandler);
      }
      // Remove label container listeners
      labelContainer.removeEventListener('mouseenter', _onMouseEnter);
      labelContainer.removeEventListener('mouseleave', _onMouseLeave);
      if (distanceLabelObject) distanceLabelObject.removeFromParent();
      if (distanceLabel.parentNode) {
        distanceLabel.parentNode.removeChild(distanceLabel);
      }
      if (target && target.parent) {
        target.parent.remove(target);
      }
      if (targetSphereGeometry) targetSphereGeometry.dispose();
      if (targetSphereMaterial) targetSphereMaterial.dispose();
      originalRemove();
    };

    group.addEventListener('removed', () => {
      // Remove scene click handler
      if (group.userData.sceneClickHandler) {
        document.removeEventListener('click', group.userData.sceneClickHandler);
      }
      // Remove label container listeners
      labelContainer.removeEventListener('mouseenter', _onMouseEnter);
      labelContainer.removeEventListener('mouseleave', _onMouseLeave);
      if (distanceLabelObject) distanceLabelObject.removeFromParent();
      if (distanceLabel.parentNode) {
        distanceLabel.parentNode.remove();
      }
      if (target && target.parent) {
        target.parent.remove(target);
        scene.remove(target);
      }
      if (targetSphereGeometry) targetSphereGeometry.dispose();
      if (targetSphereMaterial) targetSphereMaterial.dispose();
    });

    // Clear selection handler
    group.clearSelection = () => {
      editButton.classList.add('hidden');

      if (!transformControls) return;

      // Remove mouseUp listener if exists
      if (target.userData.mouseUpListener) {
        transformControls.removeEventListener('mouseUp', target.userData.mouseUpListener);
        target.userData.mouseUpListener = null;
      }

      // Check if transform controls is attached to this annotation's target
      if (transformControls.object === target) {
        const gizmo = transformControls.getHelper();
        if (gizmo) {
          if (gizmo.parent) {
            gizmo.parent.remove(gizmo);
          }
          gizmo.visible = false;
        }
        transformControls.detach();
      }
    };

    group.setOccluded = (occluded) => {
      labelContainer.style.opacity = occluded ? '0.2' : '';
    };

    return group;
  }
}
